/**
 * Teslyn Cloud's safety register (decisions 837, 863; docs/specs/safety-register.md §6;
 * 20261027180000_jsa_safety_register.sql, 20261028130000_jsa_register_followups.sql): the organisation's live entries
 * and their projects, read through the staff policies (clients and other organisations get none), and, since decision
 * 863 (3 B), recorded, changed, closed, reopened and removed here too. The database's policies decide who writes which
 * kinds (`safety_entry_writer`, the domain's `canRecordSafety`), stamp who recorded an entry and audit each step, as
 * for an entry uploaded from a laptop; the change reaches the project's laptops through their sync stream.
 *
 * Decision 1021 (R205): the register also lists each entry of the projects' Height breach reports (`height_breach`,
 * 20261104000000, read under its own staff policy: never clients), as the domain's `registerRecordFromBreach` makes
 * them, read only: they are changed and removed in the breach report on a laptop, and the register follows.
 *
 * Decision 1023: each record carries its "Show to client" choice (`safety_register_client`, 20261104230000; none until
 * someone chooses), and tenant admins and managers show or hide it (`safety_register_set_shown`, audited).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  registerSourceOf,
  safetyRegisterRecords,
  type Aircraft,
  type RegisterClientChoice,
  type RegisterRecordSource,
  type HeightBreachEntry,
  type Person,
  type ProjectCrew,
  type ProjectPhoto,
  type RegisterRecord,
  type SafetyEntry,
  type SafetyEntryDraft,
} from '@teslyn/domain';
import { missingView } from '../portal/api.ts';

/** A register record (an entry, or a breach from the Height breach report) with its project. */
export interface SafetyListRow extends RegisterRecord {
  readonly project_number: string;
  readonly project_title: string;
  /** Decision 1023: whether the client sees it (null: nobody has chosen, so not shown). */
  readonly client: RegisterClientChoice | null;
}

function fail(error: { readonly message: string } | null): void {
  if (error) throw new Error(error.message);
}

/** A row as Postgres returns it, in the shapes the domain's rules read. */
function entryOf(e: SafetyEntry): SafetyEntry {
  return {
    ...e,
    // a Postgres date or time stamp reads as text; the domain's rules read YYYY-MM-DD
    occurred_on: String(e.occurred_on).slice(0, 10),
    follow_up_due: e.follow_up_due ? String(e.follow_up_due).slice(0, 10) : null,
    people: Array.isArray(e.people) ? e.people : [],
    photo_ids: Array.isArray(e.photo_ids) ? e.photo_ids : [],
  };
}

/**
 * The organisation's height breach report entries, removed ones too (the register shows them withdrawn); none while
 * Teslyn Cloud has not got them (20261104000000 not applied).
 */
async function loadBreaches(sb: SupabaseClient, tenantId: string): Promise<HeightBreachEntry[]> {
  const { data, error } = await sb.from('height_breach').select('*').eq('tenant_id', tenantId);
  if (missingView(error)) return [];
  fail(error);
  return (Array.isArray(data) ? (data as HeightBreachEntry[]) : []).map((b) => ({
    ...b,
    history: Array.isArray(b.history) ? b.history : [],
  }));
}

/** The organisation's "Show to client" choices (decision 1023); none while Teslyn Cloud has not got them. */
async function loadChoices(sb: SupabaseClient, tenantId: string): Promise<RegisterClientChoice[]> {
  const { data, error } = await sb.from('safety_register_client').select('*').eq('tenant_id', tenantId);
  if (missingView(error)) return [];
  fail(error);
  return Array.isArray(data) ? (data as RegisterClientChoice[]) : [];
}

/** The register: the organisation's live entries and its height breach report entries, newest first. */
export async function loadSafetyEntries(sb: SupabaseClient, tenantId: string): Promise<SafetyListRow[]> {
  const [{ data, error }, breaches, choices] = await Promise.all([
    sb.from('safety_entry').select('*').eq('tenant_id', tenantId).is('deleted_at', null),
    loadBreaches(sb, tenantId),
    loadChoices(sb, tenantId),
  ]);
  const choiceOf = new Map(choices.map((c) => [`${c.source}:${c.record_id}`, c]));
  fail(error);
  const records = safetyRegisterRecords(
    ((Array.isArray(data) ? data : []) as SafetyEntry[]).map(entryOf),
    breaches,
  );
  const ids = [...new Set(records.map((e) => e.project_id))];
  const projects = new Map<string, { number: string; title: string }>();
  if (ids.length > 0) {
    const p = await sb.from('project').select('id, number, title').in('id', ids);
    fail(p.error);
    for (const r of (p.data ?? []) as { id: string; number: string; title: string }[])
      projects.set(r.id, { number: r.number, title: r.title });
  }
  return records.map((e) => ({
    ...e,
    client: choiceOf.get(`${registerSourceOf(e)}:${e.id}`) ?? null,
    project_number: projects.get(e.project_id)?.number ?? '',
    project_title: projects.get(e.project_id)?.title ?? '',
  }));
}

/** A project an entry can be recorded in: not archived, not in the Deleted bin. */
export interface SafetyProject {
  readonly id: string;
  readonly number: string;
  readonly title: string;
}

export async function loadSafetyProjects(sb: SupabaseClient, tenantId: string): Promise<SafetyProject[]> {
  const r = await sb
    .from('project')
    .select('id, number, title, status')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .neq('status', 'archived')
    .order('number');
  fail(r.error);
  return ((r.data ?? []) as SafetyProject[]).map((p) => ({ id: p.id, number: p.number, title: p.title }));
}

/** What the form chooses from in a project: the staff register, the crew, the aircraft, the properties, the photos. */
export interface SafetyChoices {
  readonly people: readonly Person[];
  readonly crew: readonly ProjectCrew[];
  readonly aircraft: readonly Pick<Aircraft, 'id' | 'registration' | 'deleted_at'>[];
  readonly parcels: readonly {
    readonly id: string;
    readonly name: string;
    readonly deleted_at: string | null;
  }[];
  readonly photos: readonly ProjectPhoto[];
}

export async function loadSafetyChoices(
  sb: SupabaseClient,
  tenantId: string,
  projectId: string,
): Promise<SafetyChoices> {
  const [people, crew, aircraft, parcels, photos] = await Promise.all([
    sb.from('person').select('*').eq('tenant_id', tenantId).is('deleted_at', null).order('name'),
    sb.from('project_crew').select('*').eq('project_id', projectId).is('deleted_at', null),
    sb
      .from('aircraft')
      .select('id, registration, deleted_at')
      .eq('tenant_id', tenantId)
      .order('registration'),
    sb.from('land_parcel').select('id, name, deleted_at').eq('project_id', projectId).is('deleted_at', null),
    // removed photos too: an entry may still link one
    sb.from('project_photo').select('*').eq('project_id', projectId),
  ]);
  for (const r of [people, crew, aircraft, parcels, photos]) fail(r.error);
  return {
    people: (people.data ?? []) as Person[],
    crew: (crew.data ?? []) as ProjectCrew[],
    aircraft: (aircraft.data ?? []) as SafetyChoices['aircraft'],
    parcels: (parcels.data ?? []) as SafetyChoices['parcels'],
    photos: (photos.data ?? []) as ProjectPhoto[],
  };
}

export type LinkedPhoto = Pick<ProjectPhoto, 'id' | 'file_name' | 'caption' | 'deleted_at'>;

/** The project photos the entries link, to name them (decision 863, 5 B). */
export async function loadLinkedPhotos(sb: SupabaseClient, ids: readonly string[]): Promise<LinkedPhoto[]> {
  if (ids.length === 0) return [];
  const r = await sb
    .from('project_photo')
    .select('id, file_name, caption, deleted_at')
    .in('id', [...ids]);
  fail(r.error);
  return (r.data ?? []) as LinkedPhoto[];
}

/** The signed-in person's name in the staff register (else their profile name or email), for "who closed it". */
export async function myStaffName(sb: SupabaseClient, tenantId: string): Promise<string> {
  const r = (await sb.rpc('my_staff_name', { p_tenant: tenantId })) as {
    data: unknown;
    error: { message: string } | null;
  };
  if (r.error) return '';
  return typeof r.data === 'string' ? r.data : '';
}

/** The columns a person writes (the guard keeps who recorded it; closing has its own call). */
function columnsOf(d: SafetyEntryDraft): SafetyEntryDraft {
  return {
    kind: d.kind,
    occurred_on: d.occurred_on,
    occurred_at: d.occurred_at.trim(),
    topic: d.topic.trim(),
    reported_by_person_id: d.reported_by_person_id,
    reported_by_name: d.reported_by_name.trim(),
    reported_by_role: d.reported_by_role.trim(),
    people: d.people,
    aircraft_id: d.aircraft_id,
    aircraft_registration: d.aircraft_registration.trim(),
    location: d.location.trim(),
    lat: d.lat,
    lon: d.lon,
    land_parcel_id: d.land_parcel_id,
    landholder_name: d.landholder_name.trim(),
    landholder_details: d.landholder_details.trim(),
    landholder_phone: d.landholder_phone.trim(),
    description: d.description.trim(),
    actions: d.actions.trim(),
    corrective_action_taken: d.corrective_action_taken,
    follow_up: d.follow_up.trim(),
    follow_up_due: d.follow_up_due,
    photo_ids: d.photo_ids,
  };
}

const READ_ONLY = 'your role reads this entry only, or it was removed';

/** Record an entry in `projectId` (no `id`), or change one; returns the row as stored. */
export async function saveSafetyEntry(
  sb: SupabaseClient,
  tenantId: string,
  projectId: string,
  d: SafetyEntryDraft,
  id: string | null,
): Promise<SafetyEntry> {
  const r = id
    ? await sb.from('safety_entry').update(columnsOf(d)).eq('id', id).select('*').maybeSingle()
    : await sb
        .from('safety_entry')
        .insert({ ...columnsOf(d), tenant_id: tenantId, project_id: projectId })
        .select('*')
        .maybeSingle();
  fail(r.error);
  if (!r.data) throw new Error(`Not saved: ${READ_ONLY}.`);
  return entryOf(r.data as SafetyEntry);
}

/** Close an entry now, by `byName`, or reopen it (`byName` null). */
export async function closeSafetyEntry(
  sb: SupabaseClient,
  id: string,
  byName: string | null,
): Promise<SafetyEntry> {
  const r = await sb
    .from('safety_entry')
    .update(
      byName === null
        ? { closed_at: null, closed_by_name: '' }
        : { closed_at: new Date().toISOString(), closed_by_name: byName.trim().slice(0, 200) },
    )
    .eq('id', id)
    .select('*')
    .maybeSingle();
  fail(r.error);
  if (!r.data) throw new Error(`Not changed: ${READ_ONLY}.`);
  return entryOf(r.data as SafetyEntry);
}

/** "Show to client" or "Hide from client" on a record (decision 1023; tenant admins and managers, audited). */
export async function setRegisterShown(
  sb: SupabaseClient,
  source: RegisterRecordSource,
  recordId: string,
  shown: boolean,
): Promise<RegisterClientChoice> {
  const r = (await sb.rpc('safety_register_set_shown', {
    p_source: source,
    p_record: recordId,
    p_shown: shown,
  })) as { data: unknown; error: { message: string } | null };
  fail(r.error);
  return r.data as RegisterClientChoice;
}

/** Remove an entry (soft delete, so the removal reaches every laptop). */
export async function removeSafetyEntry(sb: SupabaseClient, id: string): Promise<void> {
  const r = await sb
    .from('safety_entry')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .select('id')
    .maybeSingle();
  fail(r.error);
  if (!r.data) throw new Error(`Not removed: ${READ_ONLY}.`);
}
